import { pickMerchantField, toMerchantString } from "../data/merchantLegacyUtils";

export const MERCHANT_PORTAL_TOKEN_KEY = "merchant_portal_token";
export const MERCHANT_PORTAL_USER_KEY = "merchant_portal_user";

const MERCHANT_PERMISSION_KEYS = [
  "dashboard",
  "transactions",
  "settlements",
  "outlets",
  "reports",
  "users",
  "settings",
];

const MERCHANT_ROLE_PERMISSIONS = {
  company: MERCHANT_PERMISSION_KEYS,
  manage: MERCHANT_PERMISSION_KEYS,
  referral: MERCHANT_PERMISSION_KEYS,
  owner: MERCHANT_PERMISSION_KEYS,
  finance: MERCHANT_PERMISSION_KEYS,
  ops: ["dashboard", "transactions", "settlements", "outlets", "reports"],
  staff: ["dashboard", "transactions", "outlets"],
};

function uniq(values) {
  return values.filter(function filterUnique(value, index) {
    return values.indexOf(value) === index;
  });
}

export function getStoredMerchantPortalToken() {
  return localStorage.getItem(MERCHANT_PORTAL_TOKEN_KEY) || "";
}

export function setStoredMerchantPortalToken(token) {
  const normalizedToken = toMerchantString(token, "").trim();

  if (normalizedToken) {
    localStorage.setItem(MERCHANT_PORTAL_TOKEN_KEY, normalizedToken);
  } else {
    localStorage.removeItem(MERCHANT_PORTAL_TOKEN_KEY);
  }

  return normalizedToken;
}

export function clearStoredMerchantPortalSession() {
  localStorage.removeItem(MERCHANT_PORTAL_TOKEN_KEY);
  localStorage.removeItem(MERCHANT_PORTAL_USER_KEY);
}

export function mapMerchantRoleToLegacyType(role) {
  const normalizedRole = toMerchantString(role, "staff").trim().toLowerCase();

  if (normalizedRole === "owner") {
    return "company";
  }

  if (normalizedRole === "finance" || normalizedRole === "ops") {
    return "manage";
  }

  return "referral";
}

export function normalizeMerchantPermissionKey(permission) {
  const normalizedPermission = toMerchantString(permission).trim().toLowerCase();

  if (!normalizedPermission) {
    return "";
  }

  for (let index = 0; index < MERCHANT_PERMISSION_KEYS.length; index += 1) {
    const permissionKey = MERCHANT_PERMISSION_KEYS[index];

    if (
      normalizedPermission === permissionKey ||
      normalizedPermission.indexOf(permissionKey) >= 0
    ) {
      return permissionKey;
    }
  }

  return "";
}

function getMerchantUserType(user) {
  return toMerchantString(
    pickMerchantField(user, ["role", "type"]),
    "company",
  ).toLowerCase();
}

export function resolveMerchantPermissions(user) {
  const directPermissions = Array.isArray(user && user.permissions)
    ? user.permissions
        .map(normalizeMerchantPermissionKey)
        .filter(Boolean)
    : [];

  if (directPermissions.length) {
    return uniq(directPermissions);
  }

  return MERCHANT_ROLE_PERMISSIONS[getMerchantUserType(user)] || ["dashboard"];
}

export function normalizeMerchantContextStore(store, fallbackIndex) {
  const storeId = toMerchantString(pickMerchantField(store, ["id"]), "");
  const merchantName = toMerchantString(
    pickMerchantField(store, ["merchant_name", "merchantName", "name"]),
    storeId ? "Outlet " + storeId : "Outlet " + String(fallbackIndex + 1),
  );

  return {
    id: storeId,
    merchantCode: toMerchantString(
      pickMerchantField(store, ["merchant_code", "merchantCode"]),
      "",
    ),
    merchantName: merchantName,
    name: merchantName,
    status: toMerchantString(pickMerchantField(store, ["status"]), "active"),
  };
}

export function buildMerchantSession(options) {
  const auth = options && options.auth ? options.auth : {};
  const context = options && options.context ? options.context : {};
  const previousSession = options && options.previousSession ? options.previousSession : {};
  const previousBusinessProfile =
    previousSession && previousSession.businessProfile && typeof previousSession.businessProfile === "object"
      ? previousSession.businessProfile
      : {};
  const user = context.user || {};
  const business = context.business || {};
  const stores = Array.isArray(context.stores)
    ? context.stores.map(normalizeMerchantContextStore)
    : Array.isArray(previousSession.stores)
      ? previousSession.stores
      : [];
  const role = toMerchantString(
    pickMerchantField(user, ["role", "type"]),
    pickMerchantField(previousSession, ["role", "type"], "staff"),
  ).toLowerCase();
  const permissions = resolveMerchantPermissions({
    ...previousSession,
    ...user,
    role: role,
    permissions: Array.isArray(context.permissions)
      ? context.permissions
      : previousSession.permissions,
  });
  const username = toMerchantString(
    pickMerchantField(user, ["username", "account"]),
    pickMerchantField(previousSession, ["username", "account", "firstName"], ""),
  );
  const displayName = toMerchantString(
    pickMerchantField(user, ["display_name", "displayName", "name"]),
    pickMerchantField(previousSession, ["displayName", "name", "firstName"], username),
  );
  const businessId = toMerchantString(
    pickMerchantField(business, ["id"]),
    pickMerchantField(
      previousSession,
      ["businessId", "merchantBusinessId", "companyId", "id"],
      "",
    ),
  );
  const businessName = toMerchantString(
    pickMerchantField(business, ["name", "foreign_name", "foreignName"]),
    pickMerchantField(previousSession, ["businessName", "companyName"], ""),
  );
  const accessToken = toMerchantString(
    pickMerchantField(auth, ["access_token", "accessToken", "jwtToken"]),
    pickMerchantField(previousSession, ["accessToken", "jwtToken"], ""),
  );

  return {
    ...previousSession,
    id: toMerchantString(pickMerchantField(user, ["id"]), pickMerchantField(previousSession, ["id"], "")),
    account: username,
    username: username,
    firstName: username || displayName || pickMerchantField(previousSession, ["firstName"], ""),
    lastName: "",
    name: displayName || username || pickMerchantField(previousSession, ["name"], ""),
    displayName: displayName,
    email: toMerchantString(
      pickMerchantField(user, ["email"]),
      pickMerchantField(previousSession, ["email", "emailAddress"], ""),
    ),
    emailAddress: toMerchantString(
      pickMerchantField(user, ["email"]),
      pickMerchantField(previousSession, ["emailAddress", "email"], ""),
    ),
    role: role,
    type: mapMerchantRoleToLegacyType(role),
    permissions: permissions,
    enabledTwoFactor: user.two_factor_enabled ? "enabled" : "disabled",
    twoFactorEnabled:
      typeof user.two_factor_enabled === "boolean"
        ? user.two_factor_enabled
        : Boolean(previousSession.twoFactorEnabled),
    businessId: businessId,
    merchantBusinessId: businessId,
    businessIdList: businessId ? [businessId] : pickMerchantField(previousSession, ["businessIdList"], []),
    businessName: businessName,
    companyName: businessName,
    businessProfile: {
      ...previousBusinessProfile,
      id: businessId,
      name: businessName,
      foreignName: toMerchantString(
        pickMerchantField(business, ["foreign_name", "foreignName"]),
        toMerchantString(previousBusinessProfile.foreignName, ""),
      ),
      businessIdentificationNumber: toMerchantString(
        pickMerchantField(business, ["business_identification_number", "businessIdentificationNumber"]),
        pickMerchantField(previousSession, ["businessIdentificationNumber"], ""),
      ),
      settlementEmails: toMerchantString(
        pickMerchantField(business, ["settlement_emails", "settlementEmails"]),
        pickMerchantField(previousSession, ["settlementEmails"], ""),
      ),
      phoneNumber: toMerchantString(
        pickMerchantField(business, ["phone_number", "phoneNumber"]),
        pickMerchantField(previousSession, ["phoneNumber"], ""),
      ),
      activeStatus:
        typeof business.active_status === "boolean"
          ? business.active_status
          : pickMerchantField(previousSession, ["activeStatus"], true),
    },
    businessIdentificationNumber: toMerchantString(
      pickMerchantField(business, ["business_identification_number", "businessIdentificationNumber"]),
      pickMerchantField(previousSession, ["businessIdentificationNumber"], ""),
    ),
    settlementEmails: toMerchantString(
      pickMerchantField(business, ["settlement_emails", "settlementEmails"]),
      pickMerchantField(previousSession, ["settlementEmails"], ""),
    ),
    phoneNumber: toMerchantString(
      pickMerchantField(business, ["phone_number", "phoneNumber"]),
      pickMerchantField(previousSession, ["phoneNumber"], ""),
    ),
    activeStatus:
      typeof business.active_status === "boolean"
        ? business.active_status
        : pickMerchantField(previousSession, ["activeStatus"], true),
    stores: stores,
    storeIds: stores.map(function mapStoreId(storeRecord) {
      return toMerchantString(storeRecord.id);
    }),
    jwtToken: accessToken,
    accessToken: accessToken,
    tokenType: toMerchantString(
      pickMerchantField(auth, ["token_type", "tokenType"]),
      pickMerchantField(previousSession, ["tokenType"], "bearer"),
    ),
    expiresIn: pickMerchantField(auth, ["expires_in", "expiresIn"], previousSession.expiresIn || 0),
    rawContext: context,
  };
}
